import {
    OperationVariables,
    QueryLazyOptions,
    useReactiveVar,
} from '@apollo/client'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { stripDoubleSpacesTrimStart } from 'bpm/shared/utils/inputUtils'
import clsx from 'clsx'
import React, { ChangeEvent, FC, useCallback, useMemo, useState } from 'react'
import { RadioType } from './AddProductTypes'
import { AutoCompleteInput } from './AutoCompleteInput'
import {
    addProductDialogState,
    setProductDialogState,
} from './addProductFormHandler'
import { useAddProductFormStyles } from './formStyles'

type Props = {
    callAutoSuggest: (
        options?: QueryLazyOptions<OperationVariables> | undefined
    ) => void
    autoSuggestData: any
    autoSuggestLoading: boolean
}

const ExistingFamilyAutoCompleteInput: FC<Props> = ({
    callAutoSuggest,
    autoSuggestData,
    autoSuggestLoading,
}) => {
    const classes = useAddProductFormStyles()

    const [searchText, setSearchText] = useState('')

    const { firmName, errors, sectionNames, type } = useReactiveVar(
        addProductDialogState
    )

    const callAutoSuggestQuery = useCallback(
        (fetchString?: string) => {
            if (!!type) {
                callAutoSuggest({
                    variables: {
                        input: {
                            listingType: type,
                            familyName: fetchString,
                            productType: '',
                            isProductTypeSearch: false,
                            limit: 50,
                        },
                    },
                })
            }
        },
        [type, searchText, callAutoSuggest]
    )

    const sectionAutoSuggestOptions = useMemo(() => {
        return (
            autoSuggestData?.productListingDetailsAutoSuggest?.productFamilyDetails
                .reduce((acc: string[], curr: Record<string, string>) => {
                    const isMO = new RegExp(/\+/, 'g')
                    if (!!isMO.test(curr.formattedSectionName)) {
                        acc.push(
                            `(+) ${curr.sectionNumber} - ${curr.sectionName}`
                        )
                    } else {
                        acc.push(curr.formattedSectionName)
                    }
                    return acc
                }, [])
                .slice(0, 50) ?? []
        )
    }, [autoSuggestData])

    const handleChange = useCallback(
        (_, value) => {
            let tempValue = value?.replace('(+) ', '')

            setSearchText(tempValue)
            setProductDialogState({
                sectionNames: tempValue,
                model: '',
                productName: '',
                productType: '',
                productTypeRadio: RadioType.Existing,
                errors: { ...errors, sectionNames: '' },
            })
        },
        [setProductDialogState, setSearchText, errors]
    )

    const handleInputChange = useCallback(
        (e: ChangeEvent<{}>, value: string) => {
            if (e?.type === 'clear') {
                setSearchText('')
                callAutoSuggestQuery('')
            }

            if (e?.type === 'change') {
                const tempValue = value?.replace('(+) ', '')
                setSearchText(tempValue ?? '')

                if (!!errors.firmName) {
                    setProductDialogState({
                        errors: { ...errors, sectionNames: '' },
                    })
                }
                callAutoSuggestQuery(stripDoubleSpacesTrimStart(tempValue))
            }
        },
        [
            setProductDialogState,
            errors,
            callAutoSuggestQuery,
            stripDoubleSpacesTrimStart,
        ]
    )

    const handleOpen = useCallback(() => {
        callAutoSuggestQuery(stripDoubleSpacesTrimStart(sectionNames) ?? '')
    }, [callAutoSuggestQuery, sectionNames, stripDoubleSpacesTrimStart])

    return (
        <GridItem
            className={clsx(classes.rowContainer, 'rowContainer')}
            xs={12}
            data-testid="sectionNames"
        >
            <AutoCompleteInput
                disabled={!firmName}
                clearOnBlur={true}
                name="sectionNames"
                label="Family"
                valid={!errors.sectionNames || errors.sectionNames === ''}
                handleOpen={handleOpen}
                handleChange={handleChange}
                handleInputChange={handleInputChange}
                selectedValue={sectionNames}
                loading={autoSuggestLoading}
                items={sectionAutoSuggestOptions}
                getOptionSelected={(option, value) => option === value}
            />
        </GridItem>
    )
}
export default ExistingFamilyAutoCompleteInput
